
import { useState } from 'react';
import type { Product } from '../../../lib/supabase';

interface ProductCardProps {
  product: Product;
  checked?: boolean;
  onToggleCheck?: () => void;
  onDelete?: () => void;
  bulkSelectMode?: boolean;
  bulkSelected?: boolean;
  onToggleBulkSelect?: () => void;
}

/**
 * ProductCard component – displays a product with optional bulk‑selection support.
 *
 * The component now defensively handles missing data (e.g. `created_at` or `rating`)
 * and adds error‑handling for image loading failures.
 */
export function ProductCard({
  product,
  checked = false,
  onToggleCheck,
  onDelete,
  bulkSelectMode = false,
  bulkSelected = false,
  onToggleBulkSelect,
}: ProductCardProps) {
  const [bookmarked, setBookmarked] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [logoError, setLogoError] = useState(false);

  /** Formats a date string safely – falls back to “Unknown” when parsing fails. */
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Unknown';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return 'Unknown';
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  /** Renders star rating icons – guards against non‑numeric ratings. */
  const renderStars = (rating: number = 0) => {
    const safeRating = typeof rating === 'number' && !Number.isNaN(rating) ? rating : 0;
    const full = Math.floor(safeRating);
    const hasHalf = safeRating - full >= 0.5;
    const stars = [];

    for (let i = 0; i < full; i++) {
      stars.push(
        <i key={`f${i}`} className="ri-star-fill text-amber-400 text-xs"></i>
      );
    }
    if (hasHalf) {
      stars.push(
        <i key="h" className="ri-star-half-fill text-amber-400 text-xs"></i>
      );
    }
    return stars;
  };

  /** Handles click on the whole card – toggles bulk selection when appropriate. */
  const handleCardClick = () => {
    if (bulkSelectMode && onToggleBulkSelect) {
      onToggleBulkSelect();
    }
  };

  /** Default image URLs used when the provided ones fail to load. */
  const fallbackProductImage =
    'https://public.readdy.ai/ai/img_res/6d73be79b1181d44fda0fb896bc5ec12.jpg';
  const fallbackLogoImage =
    'https://readdy.ai/api/search-image?query=minimalist%20tech%20company%20logo%20icon%20simple%20geometric%20shape%20modern%20professional%20branding%20on%20white%20background&width=100&height=100&seq=logo-default&orientation=squarish';

  return (
    <div
      onClick={handleCardClick}
      className={`bg-white border shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group overflow-hidden relative ${
        bulkSelectMode
          ? bulkSelected
            ? 'border-red-400 ring-2 ring-red-400/30 cursor-pointer'
            : 'border-gray-200 cursor-pointer hover:border-red-200'
          : checked
          ? 'border-emerald-400 ring-1 ring-emerald-400/30'
          : 'border-gray-100'
      }`}
    >
      {/* Bulk select checkbox */}
      {bulkSelectMode && (
        <div className="absolute top-3 left-3 z-20">
          <div
            className={`w-6 h-6 rounded flex items-center justify-center border-2 transition-all ${
              bulkSelected
                ? 'bg-red-500 border-red-500 text-white'
                : 'bg-white/90 border-gray-300 hover:border-red-400'
            }`}
          >
            {bulkSelected && <i className="ri-check-line text-sm"></i>}
          </div>
        </div>
      )}

      {/* Image */}
      <div className="relative w-full h-[180px] overflow-hidden">
        <img
          src={imgError ? fallbackProductImage : product.image || fallbackProductImage}
          alt={product.name}
          onError={() => setImgError(true)}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>

        {/* Badges */}
        <div
          className={`absolute top-3 ${
            bulkSelectMode ? 'left-12' : 'left-3'
          } flex items-center gap-1.5 transition-all`}
        >
          {product.tags?.includes('new') && (
            <span className="px-2 py-0.5 bg-[#00FF88] text-black text-[10px] font-bold uppercase tracking-wider">
              New
            </span>
          )}
          {product.tags?.includes('trending') && (
            <span className="px-2 py-0.5 bg-amber-400 text-black text-[10px] font-bold uppercase tracking-wider">
              <i className="ri-fire-fill mr-0.5"></i>Trending
            </span>
          )}
        </div>

        {/* Top-right actions - hidden in bulk mode */}
        {!bulkSelectMode && (
          <div className="absolute top-3 right-3 flex items-center gap-1.5">
            {onToggleCheck && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleCheck();
                }}
                className={`w-8 h-8 flex items-center justify-center transition-all cursor-pointer ${
                  checked
                    ? 'bg-emerald-500 text-white shadow-md'
                    : 'bg-white/90 hover:bg-white text-gray-500 hover:text-emerald-600'
                }`}
                title={checked ? 'Remove from Products page' : 'Add to Products page'}
              >
                <i
                  className={`${
                    checked ? 'ri-check-line' : 'ri-add-circle-line'
                  } text-base`}
                ></i>
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setBookmarked((prev) => !prev);
              }}
              className="w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white text-gray-700 transition-colors cursor-pointer"
            >
              <i
                className={`${
                  bookmarked ? 'ri-bookmark-fill text-amber-500' : 'ri-bookmark-line'
                } text-base`}
              ></i>
            </button>
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                title="Delete product"
              >
                <i className="ri-delete-bin-line text-base"></i>
              </button>
            )}
          </div>
        )}

        {/* Logo */}
        <div className="absolute -bottom-5 left-4 w-10 h-10 border-2 border-white shadow-md overflow-hidden bg-white">
          <img
            src={logoError ? fallbackLogoImage : product.logo || fallbackLogoImage}
            alt={product.name}
            onError={() => setLogoError(true)}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Content */}
      <div className="pt-7 px-4 pb-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-bold text-gray-900 leading-tight">
            {product.name}
          </h3>
          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-[10px] font-medium whitespace-nowrap flex-shrink-0">
            {product.pricing_type || 'Free'}
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-2 leading-relaxed">
          {product.tagline}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {product.tags?.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 bg-gray-50 border border-gray-100 text-[10px] text-gray-500 font-medium"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Metrics */}
        <div className="flex items-center gap-3 text-[11px] text-gray-400 mb-3">
          <span className="flex items-center gap-1">
            {renderStars(product.rating ?? 0)}
            <span className="ml-0.5 font-medium text-gray-600">
              {product.rating ?? 0}
            </span>
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-group-line"></i>
            {(product.users_count ?? 0).toLocaleString()}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {formatDate(product.created_at)}
          </span>
        </div>

        {/* Pricing + CTA */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <span className="text-xs font-semibold text-gray-900">
            {product.pricing_amount || 'Free'}
          </span>
          <a
            href={product.website}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={(e) => {
              if (bulkSelectMode) e.preventDefault();
            }}
            className="px-3.5 py-1.5 bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
          >
            Visit <i className="ri-external-link-line text-xs"></i>
          </a>
        </div>
      </div>
    </div>
  );
}
